import React, { FC, ReactNode } from 'react';
import {
    vettedSeller,
    vettedDescription,
    goldSeller,
    platinumSeller,
    diamondSeller,
    goldSellerDescription,
    tieredSellerDescription,
    recognizedSeller,
    recognizedDescription,
} from '../messages/pdpDistinctionsMessages';
import { graphql, createFragmentContainer } from 'react-relay';

// components
import TopSellerIcon from 'dibs-icons/exports/Diamond';
import VettedIcon from 'dibs-icons/exports/VettedCheckmark';
import DiamondFilled from 'dibs-icons/exports/DiamondFilled';
import RecognizedIcon from 'dibs-icons/exports/RecognizedRibbon';

// styles
import styles from './PdpSharedDealerDistinction.scss';

// types
import { PdpSharedDealerDistinction_seller as Seller } from './__generated__/PdpSharedDealerDistinction_seller.graphql';

type Props = {
    seller: Seller;
};

type CreateDealerDistinctionProps = {
    icon: ReactNode;
    title: ReactNode;
    description: ReactNode;
};

const createDealerDistinction = ({
    icon,
    title,
    description,
}: CreateDealerDistinctionProps): ReactNode => (
    <>
        <div className={styles.distinction}>
            {icon}
            <span className={styles.title}>{title}</span>
        </div>
        <div className={styles.description}>{description}</div>
    </>
);

const PdpSharedDealerDistinctionComponent: FC<Props> = ({ seller }) => {
    const sellerTiers = seller?.sellerTiers;
    const isRewardsTier2 = seller?.isRewardsTier2;
    const isDistinguished = seller?.isDistinguished;
    const sellerTierDataFound = sellerTiers?.sellerTierDataNotFound;
    const isGoldSeller = !sellerTierDataFound && sellerTiers?.isGoldTier;
    const isPlatinumSeller = !sellerTierDataFound && sellerTiers?.isPlatinumTier;
    const distinctions = [];

    if (isDistinguished) {
        distinctions.push(
            createDealerDistinction({
                icon: <RecognizedIcon className={styles.icon} />,
                title: recognizedSeller,
                description: recognizedDescription,
            })
        );
    }

    if (isGoldSeller && !isRewardsTier2) {
        distinctions.push(
            createDealerDistinction({
                icon: <TopSellerIcon className={`${styles.icon} ${styles.goldDiamond}`} />,
                title: goldSeller,
                description: goldSellerDescription,
            })
        );
    } else if (isPlatinumSeller && !isRewardsTier2) {
        distinctions.push(
            createDealerDistinction({
                icon: <TopSellerIcon className={`${styles.icon} ${styles.platinumDiamond}`} />,
                title: platinumSeller,
                description: tieredSellerDescription,
            })
        );
    } else if (isRewardsTier2) {
        distinctions.push(
            createDealerDistinction({
                icon: <DiamondFilled className={styles.icon} />,
                title: diamondSeller,
                description: tieredSellerDescription,
            })
        );
    } else if (!distinctions.length) {
        distinctions.push(
            createDealerDistinction({
                icon: <VettedIcon className={styles.icon} />,
                title: vettedSeller,
                description: vettedDescription,
            })
        );
    }

    return (
        <>
            {distinctions.map((distinction, index) => (
                <div key={`distinction-${index}`} className={styles.container}>
                    {distinction}
                </div>
            ))}
        </>
    );
};

export const PdpSharedDealerDistinction = createFragmentContainer(
    PdpSharedDealerDistinctionComponent,
    {
        seller: graphql`
            fragment PdpSharedDealerDistinction_seller on Seller {
                isDistinguished
                isRewardsTier2: isAnchorListMember(listName: "SELLER_REWARDS_2")
                sellerTiers {
                    isGoldTier
                    isPlatinumTier
                    sellerTierDataNotFound
                }
            }
        `,
    }
);
